import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SyncEvent, SyncJob } from '@berytus/contracts';
import { api, dateTime, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';

const STATUS: Record<SyncJob['status'], { label: string; tone: string }> = {
  succeeded: { label: 'Concluída', tone: 'ok' },
  partial: { label: 'Parcial', tone: 'warn' },
  failed: { label: 'Falhou', tone: 'err' },
  running: { label: 'Em andamento', tone: 'info' },
  queued: { label: 'Na fila', tone: 'off' },
  cancelled: { label: 'Cancelada', tone: 'off' },
};

const KIND: Record<string, string> = { 'import.qualidoc': 'Importação de catálogo (Qualidoc)', 'api.products': 'API · envio de produtos', 'api.offers': 'API · carga de preço/estoque' };

function duration(j: SyncJob) {
  if (!j.startedAt || !j.finishedAt) return '—';
  const s = (new Date(j.finishedAt).getTime() - new Date(j.startedAt).getTime()) / 1000;
  return s < 60 ? `${s.toFixed(1)} s` : `${Math.floor(s / 60)} min ${Math.round(s % 60)} s`;
}

/** Lê um número dentro de stats sem quebrar se o formato mudar. */
function stat(j: SyncJob, ...path: string[]): number | null {
  let v: unknown = j.stats;
  for (const k of path) v = v && typeof v === 'object' ? (v as Record<string, unknown>)[k] : undefined;
  return typeof v === 'number' ? v : null;
}

export function Imports() {
  const q = useQuery({ queryKey: ['jobs'], queryFn: () => api<SyncJob[]>('/sync/jobs') });
  const [open, setOpen] = useState<string | null>(null);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Importações</h1>
          <p>Cada envio do catálogo para a Berytus fica registrado aqui, com o resultado e os casos que pedem revisão.</p>
        </div>
      </div>
      <div className="panel flush">
        {q.isPending ? (
          <div style={{ padding: 18 }}><Loading /></div>
        ) : q.error ? (
          <div style={{ padding: 18 }}><ErrorBox error={q.error} /></div>
        ) : q.data.length === 0 ? (
          <div className="empty"><b>Nenhuma importação ainda</b>Envie produtos pela API (Configuração → Integração por API) ou pela importação do seu sistema.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="simple">
              <thead>
                <tr><th>Quando</th><th>Tipo</th><th>Situação</th><th className="num">Produtos</th><th className="num">Vinculados</th><th className="num">Sem vínculo</th><th className="num">Sem EAN</th><th className="num">Kits ignorados</th><th className="num">Duração</th><th /></tr>
              </thead>
              <tbody>
                {q.data.map((j) => (
                  <JobRow key={j.id} job={j} open={open === j.id} onToggle={() => setOpen(open === j.id ? null : j.id)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function JobRow({ job: j, open, onToggle }: { job: SyncJob; open: boolean; onToggle: () => void }) {
  const n = (v: number | null) => (v == null ? '—' : int(v));
  return (
    <>
      <tr>
        <td>{dateTime(j.createdAt)}</td>
        <td>{KIND[j.kind] ?? j.kind}</td>
        <td><Tag tone={STATUS[j.status].tone}>{STATUS[j.status].label}</Tag></td>
        <td className="num">{n(stat(j, 'rows'))}</td>
        <td className="num">{n(stat(j, 'match', 'matched'))}</td>
        <td className="num">{n(stat(j, 'match', 'unmatched'))}</td>
        <td className="num">{n(stat(j, 'match', 'noGtin'))}</td>
        <td className="num">{n(stat(j, 'kitsSkipped'))}</td>
        <td className="num">{duration(j)}</td>
        <td>
          {(j.warnings > 0 || j.error) && (
            <button className="btn sm" onClick={onToggle} aria-expanded={open}>
              {open ? 'Fechar' : j.error ? 'Ver erro' : `${j.warnings} para revisar`}
            </button>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={10} style={{ background: 'var(--surface-2)' }}>
            {j.error ? <div className="error">{j.error}</div> : <Events jobId={j.id} />}
          </td>
        </tr>
      )}
    </>
  );
}

function Events({ jobId }: { jobId: string }) {
  const q = useQuery({ queryKey: ['events', jobId], queryFn: () => api<SyncEvent[]>(`/sync/jobs/${jobId}/events`) });
  if (q.isPending) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  return (
    <table className="simple">
      <tbody>
        {q.data.map((e) => (
          <tr key={e.id}>
            <td className="tnum" style={{ width: 120 }}>{e.entityRef ?? '—'}</td>
            <td>{e.message}</td>
            <td className="tnum muted">{(e.data as { gtins?: string[] } | null)?.gtins?.join(', ') ?? ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
